import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import PillTabView from '@Guidelines/Component/PillTabView';
import { theme } from '@Guidelines/Theme/theme';
import resolveDocumentStateSeverity from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/resolveDocumentStateSeverity';
import AddCandidateRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Sheets/AddFromDossierSheet/AddCandidateRow';
import PreparationSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Sheets/PreparationSheet';
import resolveElementStateSeverity from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow/Helpers/resolveElementStateSeverity';
import describeDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Helpers/describeDocument';
import ElementDescription from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/ElementDescription';
import describeInterview from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/describeInterview';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';
import useSubjectImportedDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectImportedDocuments';
import useElementDescriber from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Hooks/useElementDescriber';
import addFromDossierSheetSignal, {
	closeAddFromDossierSheet,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Signals/addFromDossierSheetSignal';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Tag, TabPanel } from '@verclam/ui-kit';
import type { TabViewTabChangeEvent } from '@verclam/ui-kit';

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const emptyStyle = css`
	margin: 0;
	padding: 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const FALLBACK_DOSSIER_NAME = 'ce dossier';

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
};

/**
 * « Ajouter depuis le dossier »: what the dossier already holds, with what the preparation already has
 * shown but not pickable, minus the documents it produced itself. Built from the interview and document
 * lists already loaded — there is no « candidates » endpoint. Up to three tabs (interviews and notes / documents / imported documents)
 * follow what the group accepts; opened for a carried-over document, it lists that type only and
 * replaces the current one.
 */
const AddFromDossierSheet: React.FC<Props> = ({ subject, preparation }) => {
	const target = useSignalValue(addFromDossierSheetSignal);
	const { data: interviews } = useSubjectInterviews(subject);
	const { data: documents } = useSubjectDocuments(subject);
	const dossierName = useSelector((state) => {
		if (subject.isBeneficiary) {
			const beneficiary = state.beneficiariesStore.beneficiary;

			return beneficiary !== null && beneficiary.id === subject.subjectId
				? beneficiary.fullName
				: FALLBACK_DOSSIER_NAME;
		}

		const loaded = state.subjectsStore.subject;

		return loaded !== null && loaded.id === subject.subjectId && loaded.title !== null
			? loaded.title
			: FALLBACK_DOSSIER_NAME;
	});
	const { labelOfDocumentType } = useElementDescriber(subject);
	const { data: importedDocuments } = useSubjectImportedDocuments(subject);
	const [tabIndex, setTabIndex] = React.useState(0);

	const replacesDocumentTypeId =
		target !== null && typeof target === 'object' ? target.replacesDocumentTypeId : null;
	const showInterviewsTab =
		replacesDocumentTypeId === null && (preparation.acceptsInterviews || preparation.acceptsNotes);
	const showDocumentsTab = replacesDocumentTypeId !== null || preparation.acceptsDocuments;
	const showImportedTab = replacesDocumentTypeId === null && preparation.acceptsDocuments;

	// Tab indexes follow the visible tabs, in order: interviews, generated documents, imported documents.
	const documentsTabIndex = showInterviewsTab ? 1 : 0;

	// The sheet opens on the tab the caller asked for, whatever tab a previous opening left selected.
	React.useEffect(() => {
		if (target === 'documents' && showDocumentsTab) {
			setTabIndex(documentsTabIndex);
		} else {
			setTabIndex(0);
		}
	}, [target, showDocumentsTab, documentsTabIndex]);

	const presentInterviewIds = React.useMemo(
		() =>
			new Set(
				preparation.sources.flatMap((element) =>
					element.interviewId !== null ? [element.interviewId] : [],
				),
			),
		[preparation.sources],
	);

	const presentDocumentIds = React.useMemo(
		() =>
			new Set(
				preparation.sources.flatMap((element) =>
					element.documentId !== null ? [element.documentId] : [],
				),
			),
		[preparation.sources],
	);

	const presentImportedDocumentIds = React.useMemo(
		() =>
			new Set(
				preparation.sources.flatMap((element) =>
					element.importedDocumentId !== null ? [element.importedDocumentId] : [],
				),
			),
		[preparation.sources],
	);

	const candidateInterviews = React.useMemo(
		() =>
			(interviews?.list ?? []).filter((interview) =>
				interview.countsAsNote ? preparation.acceptsNotes : preparation.acceptsInterviews,
			),
		[interviews, preparation.acceptsInterviews, preparation.acceptsNotes],
	);

	const candidateDocuments = React.useMemo(() => {
		const producedIds = new Set(
			preparation.documents.flatMap((document) =>
				document.documentId !== null ? [document.documentId] : [],
			),
		);

		return (documents?.list ?? []).filter(
			(document) =>
				!producedIds.has(document.id) &&
				(replacesDocumentTypeId === null || document.documentTypeId === replacesDocumentTypeId),
		);
	}, [documents, preparation.documents, replacesDocumentTypeId]);

	const candidateImportedDocuments = importedDocuments?.list ?? [];

	const handleTabChange = React.useCallback((event: TabViewTabChangeEvent) => {
		setTabIndex(event.index);
	}, []);

	const interviewList = (
		<ul css={listStyle}>
			<ConditionalLoader
				data={candidateInterviews.length > 0}
				fallback={<li css={emptyStyle}>Aucun entretien ni aucune note dans ce dossier.</li>}
			>
				{candidateInterviews.map((interview) => (
					<AddCandidateRow
						key={interview.id}
						kind={interview.countsAsNote ? 'notes' : 'interview'}
						isUsed={presentInterviewIds.has(interview.id)}
						subject={subject}
						preparationId={preparation.id}
						description={describeInterview(interview)}
						viewPath={`${buildSubjectPath(subject)}/interviews/${interview.id}`}
						interviewId={interview.id}
						documentId={null}
						importedDocumentId={null}
						replacesDocumentTypeId={null}
					/>
				))}
			</ConditionalLoader>
		</ul>
	);

	const replacedTypeLabel =
		replacesDocumentTypeId !== null ? labelOfDocumentType(replacesDocumentTypeId) : '';

	const documentList = (
		<ul css={listStyle}>
			<ConditionalLoader
				data={candidateDocuments.length > 0}
				fallback={
					<li css={emptyStyle}>
						{replacesDocumentTypeId !== null
							? `Aucun ${replacedTypeLabel} dans ce dossier.`
							: 'Aucun document dans ce dossier.'}
					</li>
				}
			>
				{candidateDocuments.map((document) => (
					<AddCandidateRow
						key={document.id}
						kind="document"
						chip={
							<Tag
								rounded
								severity={resolveDocumentStateSeverity(document.status.value)}
								value={document.status.label}
							/>
						}
						isUsed={presentDocumentIds.has(document.id)}
						subject={subject}
						preparationId={preparation.id}
						description={describeDocument(document, labelOfDocumentType(document.documentTypeId))}
						viewPath={`${buildSubjectPath(subject)}/documents/${document.id}`}
						interviewId={null}
						documentId={document.id}
						importedDocumentId={null}
						replacesDocumentTypeId={replacesDocumentTypeId}
					/>
				))}
			</ConditionalLoader>
		</ul>
	);

	const importedDocumentList = (
		<ul css={listStyle}>
			<ConditionalLoader
				data={candidateImportedDocuments.length > 0}
				fallback={<li css={emptyStyle}>Aucun document importé dans ce dossier.</li>}
			>
				{candidateImportedDocuments.map((importedDocument) => (
					<AddCandidateRow
						key={importedDocument.id}
						kind="imported"
						chip={
							<Tag
								rounded
								severity={
									importedDocument.isFailed
										? 'danger'
										: resolveElementStateSeverity(importedDocument.status.value)
								}
								value={importedDocument.status.label}
							/>
						}
						isUsed={presentImportedDocumentIds.has(importedDocument.id)}
						subject={subject}
						preparationId={preparation.id}
						description={
							new ElementDescription(
								importedDocument.name,
								`Document importé · ${importedDocument.format.label}`,
							)
						}
						viewPath={null}
						unavailableReason={importedDocument.isFailed ? importedDocument.failureMessage : null}
						interviewId={null}
						documentId={null}
						importedDocumentId={importedDocument.id}
						replacesDocumentTypeId={null}
					/>
				))}
			</ConditionalLoader>
		</ul>
	);

	let header = 'Ajouter depuis le dossier';
	let subtitle = `Ce qui a déjà été enregistré ou rédigé pour ${dossierName}, y compris dans d’autres préparations. Rien n’est généré.`;
	if (replacesDocumentTypeId !== null) {
		header = `Choisir le ${replacedTypeLabel} à reprendre`;
		subtitle = 'Le document choisi est repris tel quel. Il n’est pas modifié.';
	}

	const tabPanels: React.ReactElement[] = [];
	if (showInterviewsTab) {
		tabPanels.push(
			<TabPanel key="interviews" header={`Entretiens et notes · ${candidateInterviews.length}`}>
				{interviewList}
			</TabPanel>,
		);
	}
	if (showDocumentsTab) {
		tabPanels.push(
			<TabPanel key="documents" header={`Documents · ${candidateDocuments.length}`}>
				{documentList}
			</TabPanel>,
		);
	}
	if (showImportedTab) {
		tabPanels.push(
			<TabPanel key="imported" header={`Documents importés · ${candidateImportedDocuments.length}`}>
				{importedDocumentList}
			</TabPanel>,
		);
	}

	let content: React.ReactNode;
	if (tabPanels.length > 1) {
		content = (
			<PillTabView activeIndex={tabIndex} onTabChange={handleTabChange}>
				{tabPanels}
			</PillTabView>
		);
	} else if (showInterviewsTab) {
		content = interviewList;
	} else {
		content = documentList;
	}

	return (
		<PreparationSheet
			visible={target !== null}
			onHide={closeAddFromDossierSheet}
			header={header}
			subtitle={subtitle}
		>
			<ConditionalLoader data={target !== null}>{content}</ConditionalLoader>
		</PreparationSheet>
	);
};

export default AddFromDossierSheet;
